import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  listCommentReports,
  updateCommentReportStatus,
} from "../../services/commentReportsAdminService";
import "./AdminCommentReports.css";

function formatWhen(ts) {
  if (!ts) return "—";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-CA", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function AdminCommentReports() {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setReports(await listCommentReports({ status: filter }));
    } catch (err) {
      console.error(err);
      setError(err.message || "Could not load comment reports.");
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await updateCommentReportStatus(id, status);
      setReports((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(err.message || "Could not update report.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-comment-reports">
        <div className="admin-cr-head">
          <div>
            <p className="admin-cr-eyebrow">Moderation</p>
            <h1>Comment reports</h1>
            <p className="admin-cr-sub">
              Reports from Drop View. Reported users are never notified.
            </p>
          </div>
          <div className="admin-cr-filters">
            {["pending", "reviewed", "dismissed", "all"].map((key) => (
              <button
                key={key}
                type="button"
                className={`admin-cr-filter${filter === key ? " active" : ""}`}
                onClick={() => setFilter(key)}
              >
                {key}
              </button>
            ))}
          </div>
        </div>

        {error && <div className="admin-cr-error">{error}</div>}

        {loading ? (
          <div className="admin-cr-empty">Loading reports…</div>
        ) : reports.length === 0 ? (
          <div className="admin-cr-empty">
            No {filter === "all" ? "" : `${filter} `}comment reports.
          </div>
        ) : (
          <div className="admin-cr-list">
            {reports.map((report) => (
              <article key={report.id} className="admin-cr-card">
                <div className="admin-cr-card-top">
                  <span className="admin-cr-pill">{report.reason || "Other"}</span>
                  <span className="admin-cr-pill muted">{report.status || "pending"}</span>
                  <span className="admin-cr-when">{formatWhen(report.createdAt)}</span>
                </div>
                <p className="admin-cr-preview">
                  {report.commentTextPreview || "No comment preview."}
                </p>
                <div className="admin-cr-meta">
                  <span>Comment · {report.commentId}</span>
                  {report.dropId && (
                    <Link to={`/drop/${report.dropId}`}>Open drop ↗</Link>
                  )}
                  <span>Reported user · {report.reportedUserId}</span>
                  <span>Reporter · {report.reporterUserId}</span>
                </div>
                {report.status === "pending" && (
                  <div className="admin-cr-actions">
                    <button
                      type="button"
                      disabled={busyId === report.id}
                      onClick={() => setStatus(report.id, "reviewed")}
                    >
                      Mark reviewed
                    </button>
                    <button
                      type="button"
                      className="ghost"
                      disabled={busyId === report.id}
                      onClick={() => setStatus(report.id, "dismissed")}
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
